import { useContext, useEffect } from 'react';
import { useEventListener } from '../../../infrastructure/events/useBroadcastChannel';
import {
  TransposeRequestedEvent,
  AddSongRequestedEvent,
  DeleteSongRequestedEvent,
  EditSongPositionRequestedEvent
} from '../../../infrastructure/events/channels';
import { LineProcessor, getLocalizedKey } from '../services/line-processor';
import { JsonToSong } from '../services/json-to-song';
import { AppCtx } from '../../../AppCtx';
import useYjsDocument from '../../../shared/hooks/useYjsDocument';
import { getWorkspaceId } from '../../../shared/services/dbUtils';
import { SongMetadataKeeper } from '../services/song-metadata-keeper';
import { fetchFileContent } from '../services/songs-db';

/**
 * Component that handles song-related events globally
 * This should only be mounted once in the app to avoid duplicate event handlers
 */
/** @deprecated Song mutations are retired for the Adorans read-only workspace. */
export function SongEventHandler({ onContentChange }) {
  const { selectedFile } = useContext(AppCtx);
  const { content, updateContent } = useYjsDocument(getWorkspaceId(), selectedFile?.name);

  // Log when component mounts/unmounts
  useEffect(() => {
    console.log('🔵 SongEventHandler MOUNTED');
    return () => {
      console.log('🔴 SongEventHandler UNMOUNTED');
    };
  }, []);

  // Notify parent when content changes
  useEffect(() => {
    if (onContentChange) {
      onContentChange(content);
    }
  }, [content, onContentChange]);

  // Handle transpose events
  useEventListener(TransposeRequestedEvent, (message) => {
    if (selectedFile?.name === message.fileName && content) {
      const keeper = new SongMetadataKeeper(content);
      const sourceSong = new JsonToSong().songsEnriched(new LineProcessor().parseChordsTxt(content))[message.index];
      if (!sourceSong) return;
      // B is H in international notation and B-flat in Polish notation.
      // Persist the target key in the source notation, irrespective of the local display choice.
      const toKey = message.canonicalKey ? getLocalizedKey(message.canonicalKey, sourceSong.notation) : message.toKey;
      keeper.addOrUpdateTag(message.index, 'DisplayKey', toKey);
      updateContent(keeper.getUpdatedContent());
    }
  });

  // Handle add song events
  useEventListener(AddSongRequestedEvent, async (message) => {
    const timestamp = new Date().toISOString();
    console.log(`[${timestamp}] SongEventHandler: AddSongRequestedEvent received:`, message);
    console.trace('Event handler call stack');

    if (selectedFile?.name === message.targetFileName) {
      try {
        // Pobierz zawartość pliku źródłowego używając fetchFileContent z songs-db
        const sourceContent = await fetchFileContent(message.sourceFileName);

        if (sourceContent === null) {
          console.error(`Nie można pobrać zawartości pliku: ${message.sourceFileName}`);
          return;
        }

        // Przetwórz zawartość pliku źródłowego, aby znaleźć piosenkę o podanym numerze
        const linesProcessor = new LineProcessor();
        const jsonToSong = new JsonToSong();
        const linesProcessed = linesProcessor.parseChordsTxt(sourceContent);
        const songsEnriched = jsonToSong.songsEnriched(linesProcessed);

        // Znajdź piosenkę o podanym numerze
        const selectedSong = songsEnriched.find(song => song.currentPosition === message.songNumber);

        if (selectedSong) {
          console.log('Znaleziono piosenkę do dodania:', selectedSong);
          // Konwertuj linie piosenki na tekst
          const songContent = jsonToSong.songToText(selectedSong);

          // Dodaj piosenkę na końcu pliku docelowego
          // Jeśli plik jest pusty lub zawiera tylko białe znaki, nie dodawaj pustych linii na początku
          const trimmedContent = content.trim();
          const updatedContent = trimmedContent ? content + '\n\n' + songContent : songContent;

          // Zaktualizuj zawartość pliku - useEffect will handle processContent automatically
          updateContent(updatedContent);
        } else {
          console.error('Nie znaleziono piosenki o numerze:', message.songNumber);
        }
      } catch (error) {
        console.error('Błąd podczas dodawania piosenki:', error);
      }
    }
  });

  // Handle delete song events
  useEventListener(DeleteSongRequestedEvent, (message) => {
    console.log('DeleteSongRequestedEvent received:', message);

    if (selectedFile?.name === message.fileName && content) {
      try {
        // Use the SongMetadataKeeper to get the raw content split into songs
        const keeper = new SongMetadataKeeper(content);

        // Remove the song at the given index
        keeper.removeSong(message.songIndex);

        // Get the updated content without the deleted song
        const updatedContent = keeper.getUpdatedContent();

        // Update the file content
        updateContent(updatedContent);

        console.log('Piosenka została usunięta');
      } catch (error) {
        console.error('Błąd podczas usuwania piosenki:', error);
      }
    }
  });

  // Handle edit song position events
  useEventListener(EditSongPositionRequestedEvent, (message) => {
    if (selectedFile?.name === message.targetFileName && content) {
      try {
        const keeper = new SongMetadataKeeper(content);

        // Przesuń piosenkę na nową pozycję
        keeper.moveSong(message.oldPosition - 1, message.newPosition - 1);

        // Pobierz zaktualizowaną zawartość
        const updatedContent = keeper.getUpdatedContent();

        // Zaktualizuj zawartość pliku
        updateContent(updatedContent);
      } catch (error) {
        console.error('Błąd podczas przesuwania piosenki:', error);
      }
    }
  });

  // This component doesn't render anything
  return null;
}
